import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  buildImageTransformStyle,
  getImageCrop,
  getImageUrl,
} from '../lib/imageData';
import ImageWatermark from './ImageWatermark';

export default function CroppedImage({
  image,
  alt = '',
  containerClassName = '',
  imgClassName = '',
  style,
  onLoad,
  onError,
  refreshOnError = true,
  refreshEndpoint = '/api/cloudinary/sign',
  protectSaving = true,
  watermark = false,
  ...imgProps
}) {
  const url = getImageUrl(image);
  const [resolvedUrl, setResolvedUrl] = useState(url);
  const refreshAttemptedRef = useRef(false);
  const crop = getImageCrop(image);
  const shellClassName = protectSaving ? 'protected-image-shell' : '';
  const containerClasses = `relative overflow-hidden ${protectSaving ? 'select-none' : ''} ${containerClassName}`.trim();
  const containerRef = useRef(null);
  const [layout, setLayout] = useState({ width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 });
  const watermarkConfig = watermark && typeof watermark === 'object' ? watermark : watermark ? {} : null;
  const {
    onContextMenu: imgOnContextMenu,
    onDragStart: imgOnDragStart,
    draggable: imgDraggable,
    ...restImgProps
  } = imgProps;
  function addRefreshParam(nextUrl) {
    const separator = nextUrl.includes('?') ? '&' : '?';
    return `${nextUrl}${separator}refresh=${Date.now()}`;
  }
  function handleContextMenu(event) {
    if (protectSaving) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (typeof imgOnContextMenu === 'function') {
      imgOnContextMenu(event);
    }
  }
  function handleDragStart(event) {
    if (protectSaving) {
      event.preventDefault();
    }
    if (typeof imgOnDragStart === 'function') {
      imgOnDragStart(event);
    }
  }
  const protectionStyles = protectSaving ? (
    <style jsx>{`
      .protected-image-shell :global(img) {
        -webkit-user-drag: none;
        -khtml-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
      }

      .protected-image-shell {
        -webkit-touch-callout: none;
      }

      @media print {
        .protected-image-shell {
          display: none !important;
        }
      }
    `}</style>
  ) : null;
  const measureContainer = useCallback(() => {
    const node = containerRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const width = rect.width || node.clientWidth || 0;
    const height = rect.height || node.clientHeight || 0;
    setLayout((prev) =>
      prev.width === width && prev.height === height ? prev : { ...prev, width, height }
    );
  }, []);
  const rectStyle = useMemo(() => {
    if (!crop?.rect) return null;
    const { width, height, naturalWidth, naturalHeight } = layout;
    if (!width || !height || !naturalWidth || !naturalHeight) return null;
    const rx = crop.rect.x * naturalWidth;
    const ry = crop.rect.y * naturalHeight;
    const rw = crop.rect.width * naturalWidth;
    const rh = crop.rect.height * naturalHeight;
    if (!rw || !rh) return null;
    const scale = Math.max(width / rw, height / rh);
    const left = (width - rw * scale) / 2 - rx * scale;
    const top = (height - rh * scale) / 2 - ry * scale;
    return {
      width: `${naturalWidth * scale}px`,
      height: `${naturalHeight * scale}px`,
      left: `${left}px`,
      top: `${top}px`,
      maxWidth: 'none',
      ...style,
    };
  }, [crop, layout, style]);

  useEffect(() => {
    setResolvedUrl(url);
    refreshAttemptedRef.current = false;
  }, [url]);

  useEffect(() => {
    if (!crop?.rect) return;
    setLayout((prev) =>
      prev.naturalWidth === 0 && prev.naturalHeight === 0
        ? prev
        : { ...prev, naturalWidth: 0, naturalHeight: 0 }
    );
  }, [crop?.rect, url]);

  useEffect(() => {
    if (!crop?.rect || !containerRef.current) return undefined;
    measureContainer();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const entry = entries?.[0];
        if (!entry) return;
        const width = entry.contentRect?.width || 0;
        const height = entry.contentRect?.height || 0;
        setLayout((prev) =>
          prev.width === width && prev.height === height ? prev : { ...prev, width, height }
        );
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measureContainer);
    return () => window.removeEventListener('resize', measureContainer);
  }, [crop?.rect, measureContainer]);

  async function handleImageError(event) {
    if (refreshOnError && !refreshAttemptedRef.current && url) {
      const isCloudinaryUrl = /^https?:\/\/res\.cloudinary\.com\//i.test(url);
      if (isCloudinaryUrl) {
        refreshAttemptedRef.current = true;
        try {
          const response = await fetch(`${refreshEndpoint}?src=${encodeURIComponent(url)}`, {
            cache: 'no-store',
          });
          const data = await response.json().catch(() => ({}));
          const nextUrl = String(data?.url || '').trim();
          if (response.ok && nextUrl) {
            setResolvedUrl(addRefreshParam(nextUrl));
            return;
          }
        } catch (_) {
          // fall through to final error callback
        }
      }
    }

    if (typeof onError === 'function') {
      onError(event);
    }
  }

  if (!resolvedUrl) return null;

  if (!crop) {
    return (
      <div
        ref={containerRef}
        className={`${containerClasses} ${shellClassName}`.trim()}
        onContextMenu={protectSaving ? handleContextMenu : undefined}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={resolvedUrl}
          alt={alt}
          className={`relative z-10 ${imgClassName}`.trim()}
          style={style}
          draggable={protectSaving ? false : imgDraggable}
          onLoad={onLoad}
          onError={handleImageError}
          onContextMenu={protectSaving ? handleContextMenu : imgOnContextMenu}
          onDragStart={protectSaving ? handleDragStart : imgOnDragStart}
          {...restImgProps}
        />
        {watermarkConfig ? (
          <ImageWatermark
            logoSrc={watermarkConfig.logoSrc || '/logos/logo.svg'}
            opacity={typeof watermarkConfig.opacity === 'number' ? watermarkConfig.opacity : 0.15}
            backgroundSize={watermarkConfig.backgroundSize || '120px'}
            className={watermarkConfig.className || ''}
          />
        ) : null}
        {protectionStyles}
      </div>
    );
  }

  if (crop.rect) {
    return (
      <div
        ref={containerRef}
        className={`${containerClasses} ${shellClassName}`.trim()}
        onContextMenu={protectSaving ? handleContextMenu : undefined}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={resolvedUrl}
          alt={alt}
          className={`absolute left-0 top-0 z-10 max-w-none ${imgClassName}`.trim()}
          style={rectStyle || style}
          draggable={protectSaving ? false : imgDraggable}
          onLoad={(event) => {
            const naturalWidth = event.currentTarget.naturalWidth || 0;
            const naturalHeight = event.currentTarget.naturalHeight || 0;
            measureContainer();
            setLayout((prev) =>
              prev.naturalWidth === naturalWidth && prev.naturalHeight === naturalHeight
                ? prev
                : { ...prev, naturalWidth, naturalHeight }
            );
            if (typeof onLoad === 'function') {
              onLoad(event);
            }
          }}
          onError={handleImageError}
          onContextMenu={protectSaving ? handleContextMenu : imgOnContextMenu}
          onDragStart={protectSaving ? handleDragStart : imgOnDragStart}
          {...restImgProps}
        />
        {watermarkConfig ? (
          <ImageWatermark
            logoSrc={watermarkConfig.logoSrc || '/logos/logo.svg'}
            opacity={typeof watermarkConfig.opacity === 'number' ? watermarkConfig.opacity : 0.15}
            backgroundSize={watermarkConfig.backgroundSize || '120px'}
            className={watermarkConfig.className || ''}
          />
        ) : null}
        {protectionStyles}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`${containerClasses} ${shellClassName}`.trim()}
      onContextMenu={protectSaving ? handleContextMenu : undefined}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={resolvedUrl}
        alt={alt}
        className={`absolute left-1/2 top-1/2 z-10 h-full w-full max-w-none ${imgClassName}`.trim()}
        style={buildImageTransformStyle(image, style)}
        draggable={protectSaving ? false : imgDraggable}
        onLoad={onLoad}
        onError={handleImageError}
        onContextMenu={protectSaving ? handleContextMenu : imgOnContextMenu}
        onDragStart={protectSaving ? handleDragStart : imgOnDragStart}
        {...restImgProps}
      />
      {watermarkConfig ? (
        <ImageWatermark
          logoSrc={watermarkConfig.logoSrc || '/logos/logo.svg'}
          opacity={typeof watermarkConfig.opacity === 'number' ? watermarkConfig.opacity : 0.15}
          backgroundSize={watermarkConfig.backgroundSize || '120px'}
          className={watermarkConfig.className || ''}
        />
      ) : null}
      {protectionStyles}
    </div>
  );
}
